import React, { useState } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import CustomDialog from '../../_common/CustomDialog'
import CustomSwitchField from '../../_common/_form_fields/CustomSwitchField'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomTextField from '../../_common/_form_fields/CustomTextField'
import CustomCheckboxField from '../../_common/_form_fields/CustomCheckboxField'

const ConfigBillingGeneralConfig = (props) => {
  const {
    supportedPaymentMethods,
    enableBillingService,
    setEnableBillingService,
    defaultAccountType,
    setDefaultAccountType,
    postpaidInvoiceType,
    setPostpaidInvoiceType,
    postpaidInvoicePayPeriod,
    setPostpaidInvoicePayPeriod,
    allowPublicCalculator,
    setAllowPublicCalculator,
    currency,
    setCurrency,
    enableVat,
    setEnableVat,
    pricesIncludeVat,
    setPricesIncludeVat,
    vatTax,
    setVatTax,
    errors,
    setErrors,
    submitted,
    validateFormFields,
  } = props
  const theme = useTheme()

  const [expanded, setExpanded] = useState(true)
  const [helperDialogOpen, setHelperDialogOpen] = useState(false)
  const [helperText, setHelperText] = useState('')

  const defaultAccountTypeList = [
    {
      keyword: defaultTexts.prepaid,
      value: 'prepaid',
    },
    {
      keyword: defaultTexts.postpaid,
      value: 'postpaid',
    },
  ]

  const postPaidInvoiceTypeList = [
    {
      keyword: defaultTexts.auto,
      value: 'auto',
    },
    {
      keyword: defaultTexts.manual,
      value: 'manual',
    },
  ]

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const helpIconStyle = {
    color: theme.palette.oxfordBlue,
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const switchLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    fontWeight: '500',
  }

  const checkboxStyle = {
    color: theme.palette.violetsAreBlue,

    '&.Mui-checked': {
      color: theme.palette.violetsAreBlue,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  }

  const checkboxLabelStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
  }

  const getError = (field_key) => {
    const error_object = errors.find(item => item.field_key === field_key)
    return error_object ? true : false
  }

  const getCurrencyList = () => {
    let currency_list = []
    let currency_names = []

    supportedPaymentMethods.forEach(pm => {
      if (pm.currencies && pm.currencies.length > 0) {
        pm.currencies.forEach(cu => {
          if (!currency_names.includes(cu.name)) {
            currency_names.push(cu.name)
            currency_list.push({
              keyword: cu.name.toUpperCase(),
              value: cu.name,
            })
          }
        })
      }
    })

    return currency_list
  }

  const handleHelperDialogOpen = (value) => {
    setHelperDialogOpen(true)
    setHelperText(value)
  }

  const handleHelperDialogClose = () => {
    setHelperDialogOpen(false)
    setHelperText('')
  }

  const handleChangeEnableBillingService = (event) => {
    setEnableBillingService(event.target.checked)
  }

  const handleChangeDefaultAccountType = (value) => {
    setDefaultAccountType(value)
    validateFormFields()
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== 'defaultAccountType'))
    }
  }

  const handleChangePostpaidInvoiceType = (value) => {
    setPostpaidInvoiceType(value)
    validateFormFields()
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== 'postpaidInvoiceType'))
    }
  }

  const handleChangePostpaidInvoicePayPeriod = (e) => {
    const value = e.target.value
    setPostpaidInvoicePayPeriod(value)
    validateFormFields()
  }

  const handleChangeAllowPublicCalculator = (value) => {
    setAllowPublicCalculator(value)
  }

  const handleChangeCurrency = (value) => {
    setCurrency(value)
    validateFormFields()
    if (value) {
      setErrors(prev => prev.filter(item => item.field_key !== 'currency'))
    }
  }

  const handleChangeEnableVat = (value) => {
    setEnableVat(value)
  }

  const handleChangePricesIncludeVat = (value) => {
    setPricesIncludeVat(value)
  }

  const handleChangeVatTax = (e) => {
    const value = e.target.value
    if (value && value.match(/^[0-9]*\.?[0-9]*$/)) {
      setVatTax(value)
    }
    validateFormFields()
  }

  return (
    <>
      <Box sx={wrapperStyle}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={headStyle}
        >
          <Box sx={labelStyle}>
            <Box>{defaultTexts.generalConfiguration}</Box>
          </Box>
          <Box sx={{ paddingRight: '10px ' }}>
            {
              expanded ?
              <IconButton onClick={() => setExpanded(false)}>
                <FaMinimize color={theme.palette.greenSheen} />
              </IconButton> :
              <IconButton onClick={() => setExpanded(true)}>
                <FaMaximize color={theme.palette.greenSheen} />
              </IconButton>
            }
          </Box>
        </Stack>

        {
          expanded &&
          <Box sx={bodyStyle}>
            <Grid container spacing={{ xs: 0, lg: 6, xl: 18 }}>
              <Grid size={{ xs: 12, lg: 5 }}>

                <Box sx={fieldBoxStyle}>
                  <CustomSwitchField
                    label={defaultTexts.enableBillingService}
                    checked={enableBillingService}
                    handleChange={handleChangeEnableBillingService}
                    sxLabel={switchLabelStyle}
                  />
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-start'
                    spacing={1}
                  >
                    <Box sx={{ width: '90%' }}>
                      <CustomSelectField
                        label={defaultTexts.defaultAccountType}
                        items={defaultAccountTypeList} 
                        currentValue={defaultAccountType}
                        setCurrentValue={handleChangeDefaultAccountType}
                        required={true}
                        error={getError('defaultAccountType') && submitted}
                        self_item_titles={true}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Box>                    
                    <IconButton
                      sx={helpIconStyle}
                      onClick={() => handleHelperDialogOpen(defaultTexts.defaultAccountTypeHelperText)}
                    >
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-start'
                    spacing={1}
                  >
                    <Box sx={{ width: '90%' }}>
                      <CustomSelectField
                        label={defaultTexts.postpaidInvoiceType}
                        items={postPaidInvoiceTypeList} 
                        currentValue={postpaidInvoiceType}
                        setCurrentValue={handleChangePostpaidInvoiceType}
                        required={true}
                        error={getError('postpaidInvoiceType') && submitted}
                        self_item_titles={true}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Box>                      
                    <IconButton
                      sx={helpIconStyle}
                      onClick={() => handleHelperDialogOpen(defaultTexts.postpaidInvoiceTypeHelperText)}
                    >
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-start'
                    spacing={1}
                  >
                    <Box sx={{ width: '90%' }}>
                      <CustomTextField
                        type='number'
                        min={1}
                        variant='outlined'
                        label={defaultTexts.postpaidInvoicePayPeriod}
                        currentValue={postpaidInvoicePayPeriod}
                        setCurrentValue={handleChangePostpaidInvoicePayPeriod}
                        required={true}
                        error={getError('postpaidInvoicePayPeriod') && submitted}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Box>                      
                    <IconButton
                      sx={helpIconStyle}
                      onClick={() => handleHelperDialogOpen(defaultTexts.postpaidInvoicePayPeriodHelperText)}
                    >
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Stack>
                </Box>

                <Box sx={fieldBoxStyle}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-start'
                    spacing={1}
                  >
                    <Box sx={{ width: '90%' }}>
                      <CustomCheckboxField
                        label={defaultTexts.allowPublicPriceCalculator}
                        checked={allowPublicCalculator}
                        handleChange={handleChangeAllowPublicCalculator}
                        sx={checkboxStyle}
                        sxLabel={checkboxLabelStyle}
                      />
                    </Box>                    
                    <IconButton
                      sx={helpIconStyle}
                      onClick={() => handleHelperDialogOpen(defaultTexts.allowPublicPriceCalculatorHelperText)}
                    >
                      <HelpOutlineIcon fontSize='large' />
                    </IconButton>
                  </Stack>
                </Box>

              </Grid>
              <Grid size={{ xs: 12, lg: 7, xl: 5 }}>

                <Box sx={fieldBoxStyle}>
                  <CustomSelectField
                    label={defaultTexts.currency}
                    items={getCurrencyList()}
                    currentValue={currency}
                    setCurrentValue={handleChangeCurrency}
                    required={true}
                    error={getError('currency') && submitted}
                    self_item_titles={true}
                    size='small'
                    sx={formControlStyle}
                    outlinedInputSx={outlinedInputStyle}
                  />
                </Box>

                <Box sx={fieldBoxStyle}>
                  <CustomCheckboxField
                    label={defaultTexts.enableVatTax}
                    checked={enableVat}
                    handleChange={handleChangeEnableVat}
                    sx={checkboxStyle}
                    sxLabel={checkboxLabelStyle}
                  />
                </Box>

                {
                  enableVat &&
                  <>
                    <Box sx={fieldBoxStyle}>
                      <CustomCheckboxField
                        label={defaultTexts.pricesIncludeVatTax}
                        checked={pricesIncludeVat}
                        handleChange={handleChangePricesIncludeVat}
                        sx={checkboxStyle}
                        sxLabel={checkboxLabelStyle}
                      />
                    </Box>

                    <Box sx={fieldBoxStyle}>
                      <CustomTextField 
                        variant='outlined'
                        label={defaultTexts.vatTaxPerc}
                        currentValue={vatTax}
                        setCurrentValue={handleChangeVatTax}
                        required={true}
                        error={getError('vatTax') && submitted}
                        size='small'
                        sx={formControlStyle}
                        outlinedInputSx={outlinedInputStyle}
                      />
                    </Box>
                  </>
                }                

              </Grid>
            </Grid>
          </Box>
        }
      </Box>

      <CustomDialog
        open={helperDialogOpen}
        onClose={handleHelperDialogClose}
        dialogBody={{
          text: helperText,
        }}
      />
    </>
  )
}

export default ConfigBillingGeneralConfig